import type { Client, Job } from '../../data/types';

export interface ArmpPesticide { product: string; type: string; active: string; applicationMethod: string }
export interface ArmpHazard { hazard: string; likelihood: string; consequence: string; riskType: string; controlMeasures: string }
export interface ArmpPerson { person: string; role: string; responsibility: string }

/** Activity Risk Management Plan for work at high-risk or sensitive places (Queensland requirements). */
export interface ArmpData {
  preparer: { name: string; phone: string; licence: string };
  technician: { sameAsPreparer: boolean; name: string; phone: string; licence: string };
  customer: { name: string; phone: string; email: string };
  place: { classification: string; address: string; type: string; accessController: string; accessPhone: string; accessProcedure: string; workArea: string; additionalInformation: string };
  activity: { date: string; time: string; reason: string; pests: string; pesticides: ArmpPesticide[]; precautions: string; estimatedDuration: { days: string; hours: string; minutes: string }; additionalInformation: string };
  hazards: { items: ArmpHazard[]; additionalInformation: string };
  responsibilities: { people: ArmpPerson[]; additionalInformation: string };
  endorsement: { preparerName: string; planDate: string; technicianName: string; technicianDate: string; confirmed: boolean };
}

export const LIKELIHOOD = [['A', 'Almost certain'], ['B', 'Likely'], ['C', 'Moderate'], ['D', 'Unlikely'], ['E', 'Rare'], ['F', 'Never']];
export const CONSEQUENCE = [['1', 'Insignificant'], ['2', 'Minor'], ['3', 'Moderate'], ['4', 'Major'], ['5', 'Catastrophic']];
const RISK_MATRIX: Record<string, string[]> = {
  A: ['Moderate', 'High', 'Severe', 'Severe', 'Severe'],
  B: ['Moderate', 'High', 'High', 'Severe', 'Severe'],
  C: ['Low', 'Moderate', 'High', 'Severe', 'Severe'],
  D: ['Low', 'Low', 'Moderate', 'High', 'Severe'],
  E: ['Low', 'Low', 'Moderate', 'High', 'Severe'],
  F: ['Low', 'Low', 'Moderate', 'High', 'High']
};
export const riskType = (likelihood: string, consequence: string) => RISK_MATRIX[likelihood]?.[Number(consequence) - 1] || 'Select ratings';

export const emptyPesticide = (): ArmpPesticide => ({ product: '', type: '', active: '', applicationMethod: '' });
export const emptyHazard = (): ArmpHazard => ({ hazard: '', likelihood: '', consequence: '', riskType: 'Select ratings', controlMeasures: '' });
export const emptyPerson = (): ArmpPerson => ({ person: '', role: '', responsibility: '' });

export function newArmp(job: Job, client: Client | null): ArmpData {
  return {
    preparer: { name: job.techName, phone: '', licence: '' },
    technician: { sameAsPreparer: false, name: job.techName, phone: '', licence: '' },
    customer: { name: client?.contact || client?.name || job.clientName, phone: client?.phone || '', email: client?.email || '' },
    place: { classification: 'High-risk place', address: client?.address || '', type: '', accessController: '', accessPhone: '', accessProcedure: '', workArea: '', additionalInformation: '' },
    activity: { date: job.date, time: job.startTime, reason: job.notes, pests: job.detail || job.serviceCode, pesticides: [emptyPesticide()], precautions: '', estimatedDuration: { days: '', hours: '', minutes: '' }, additionalInformation: '' },
    hazards: { items: [emptyHazard()], additionalInformation: '' },
    responsibilities: { people: [emptyPerson()], additionalInformation: '' },
    endorsement: { preparerName: job.techName, planDate: '', technicianName: job.techName, technicianDate: '', confirmed: false }
  };
}

/** Starts a revision from a previously saved ARMP (merged over defaults so older shapes still load). */
export function reviseArmp(saved: Record<string, unknown>, job: Job, client: Client | null): ArmpData {
  const base = newArmp(job, client);
  const merged = { ...base } as Record<string, unknown>;
  for (const key of Object.keys(base) as Array<keyof ArmpData>) {
    if (saved[key] && typeof saved[key] === 'object') merged[key] = { ...(base[key] as object), ...(saved[key] as object) };
  }
  const result = merged as unknown as ArmpData;
  if (!result.activity.pesticides?.length) result.activity.pesticides = [emptyPesticide()];
  if (!result.hazards.items?.length) result.hazards.items = [emptyHazard()];
  if (!result.responsibilities.people?.length) result.responsibilities.people = [emptyPerson()];
  result.endorsement = { ...result.endorsement, confirmed: false };
  return result;
}

const hasContent = (row: object) => Object.entries(row).some(([key, value]) => key !== 'riskType' && String(value).trim());
export function cleanArmp(data: ArmpData): ArmpData {
  return {
    ...data,
    activity: { ...data.activity, pesticides: data.activity.pesticides.filter(hasContent) },
    hazards: { ...data.hazards, items: data.hazards.items.filter(hasContent) },
    responsibilities: { ...data.responsibilities, people: data.responsibilities.people.filter(hasContent) }
  };
}
